import { startOfToday } from "date-fns";
import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import useVisitDragDrop from "~/hooks/useVisitDragDrop";

type Full = ReturnType<typeof useVisitDragDrop>;
type LocalValues = {
  calendarBlocks: Full["localCalendarBlocks"]
  loading: Full["loading"]
  shifts: Full["localShifts"]
  visits: Full["localVisits"]
  visitResources: Full["localVisitResources"]
}
const TodaysVisitsContext = createContext<LocalValues>({
  calendarBlocks: [],
  loading: true,
  shifts: [],
  visits: [],
  visitResources: []
});

/*
 * A polling, up to date view of today's schedule
 */
export const useTodaysVisits = () => useContext(TodaysVisitsContext)!;

// This component reuses the command center's drag and drop hook to Provide
// various components with a shared mechanism to keep their information up to
// date - without duplicating the polling for every component.
export const TodaysVisitsProvider = ({ children }: PropsWithChildren) => {
  const today = useMemo(startOfToday, []);
  const state = useVisitDragDrop(today, 60_000);

  // This view is designed to be read-only; we therefore provider
  // only the data from the server, without dispatch or other local modifications.
  const localState = useMemo(() => ({
    calendarBlocks: state.localCalendarBlocks,
    loading: state.loading,
    shifts: state.localShifts,
    visits: state.localVisits,
    visitResources: state.localVisitResources,
  } as const), [state]);

  return (
    <TodaysVisitsContext.Provider value={localState}>
      {children}
    </TodaysVisitsContext.Provider>
  )
}
